const parsedMaxFields = Number(process.env.REACT_APP_STRUCTURED_OUTPUT_MAX_FIELDS)
const MAX_FIELDS =
    Number.isFinite(parsedMaxFields) && parsedMaxFields > 0 ? parsedMaxFields : 50

export const SCHEMA_LIMITS = {
    MAX_FIELDS,
    FIELD_NAME_MIN: 1,
    FIELD_NAME_MAX: 64,
    FIELD_NAME_PATTERN: /^[a-zA-Z_][a-zA-Z0-9_]*$/,
    DESCRIPTION_MAX: 256,
    SCHEMA_NAME_MAX: 128,
    ENUM_OPTIONS_MIN: 1,
    ENUM_OPTIONS_MAX: 20,
    ENUM_OPTION_LENGTH_MAX: 64,
    MAX_NESTING_DEPTH: 5,
}

export const FIELD_TYPE_OPTIONS = [
    { value: "string", label: "String" },
    { value: "number", label: "Number" },
    { value: "object", label: "Object (nested)" },
    { value: "boolean", label: "Boolean" },
    { value: "enum", label: "Enum" },
    { value: "array", label: "Array" },
]

export const ARRAY_ITEMS_TYPE_OPTIONS = [
    { value: "string", label: "String" },
    { value: "number", label: "Number" },
    { value: "boolean", label: "Boolean" },
]

export const createEmptyField = () => ({
    id: crypto.randomUUID(),
    name: "",
    type: "string",
    description: "",
    required: true,
})

const validateFields = (fields, depth, errors) => {
    const seen = new Set()
    for (const field of fields) {
        const keyFor = (k) => `${field.id}.${k}`

        if (!field.name.trim()) {
            errors[keyFor("name")] = "Field name is required"
        } else if (!SCHEMA_LIMITS.FIELD_NAME_PATTERN.test(field.name)) {
            errors[keyFor("name")] =
                "Must start with a letter or underscore, only alphanumeric and underscores"
        } else if (field.name.length > SCHEMA_LIMITS.FIELD_NAME_MAX) {
            errors[keyFor("name")] = `Max ${SCHEMA_LIMITS.FIELD_NAME_MAX} characters`
        } else if (seen.has(field.name.toLowerCase())) {
            errors[keyFor("name")] = "Duplicate field name"
        }
        seen.add(field.name.toLowerCase())

        if (field.description && field.description.length > SCHEMA_LIMITS.DESCRIPTION_MAX) {
            errors[keyFor("description")] = `Max ${SCHEMA_LIMITS.DESCRIPTION_MAX} characters`
        }

        if (field.type === "enum") {
            const opts = field.options || []
            if (opts.length < SCHEMA_LIMITS.ENUM_OPTIONS_MIN) {
                errors[keyFor("options")] = "At least 1 option is required"
            } else if (opts.length > SCHEMA_LIMITS.ENUM_OPTIONS_MAX) {
                errors[keyFor("options")] = `Max ${SCHEMA_LIMITS.ENUM_OPTIONS_MAX} options`
            } else if (new Set(opts).size !== opts.length) {
                errors[keyFor("options")] = "Options must be unique"
            }
        }

        if (field.type === "array" && !field.items_type) {
            errors[keyFor("items_type")] = "Items type is required"
        }

        if (field.type === "object") {
            const nested = field.fields || []
            if (nested.length === 0) {
                errors[keyFor("fields")] = "Object must have at least 1 nested field"
            } else if (nested.length > SCHEMA_LIMITS.MAX_FIELDS) {
                errors[keyFor("fields")] = `Max ${SCHEMA_LIMITS.MAX_FIELDS} nested fields`
            }
            if (depth + 1 > SCHEMA_LIMITS.MAX_NESTING_DEPTH) {
                errors[keyFor("fields")] = `Max nesting depth is ${SCHEMA_LIMITS.MAX_NESTING_DEPTH}`
            } else {
                validateFields(nested, depth + 1, errors)
            }
        }
    }
}

export const validateSchema = (schema) => {
    const errors = {}
    if (!schema || !schema.fields) return errors
    validateFields(schema.fields, 1, errors)
    return errors
}

const hydrateFields = (fields) =>
    (fields || []).map((f) => ({
        ...f,
        id: f.id || crypto.randomUUID(),
        ...(f.type === "object" && { fields: hydrateFields(f.fields) }),
    }))

export const hydrateSchema = (apiSchema) => {
    if (!apiSchema || !apiSchema.fields) {
        return { schema_name: "", fields: [] }
    }
    return {
        schema_name: apiSchema.schema_name || "",
        fields: hydrateFields(apiSchema.fields),
    }
}

export const canonicalFields = (fields) =>
    (fields || []).map((f) => {
        const out = {
            name: f?.name ?? "",
            type: f?.type,
            required: f?.required ?? true,
        }
        if (f?.description) out.description = f.description
        if (f?.type === "enum") out.options = f.options || []
        if (f?.type === "array") out.items_type = f.items_type
        if (f?.type === "object") out.fields = canonicalFields(f.fields)
        return out
    })

export const canonicalSchema = (s) => {
    if (!s || !s.fields || s.fields.length === 0) return null
    return {
        schema_name: s.schema_name || "",
        fields: canonicalFields(s.fields),
    }
}

export const deepEqualUnordered = (a, b) => {
    if (a === b) return true
    if (a === null || b === null) return a === b
    if (typeof a !== typeof b) return false
    if (typeof a !== "object") return a === b
    if (Array.isArray(a) !== Array.isArray(b)) return false
    if (Array.isArray(a)) {
        if (a.length !== b.length) return false
        return a.every((item, i) => deepEqualUnordered(item, b[i]))
    }
    const aKeys = Object.keys(a)
    const bKeys = Object.keys(b)
    if (aKeys.length !== bKeys.length) return false
    return aKeys.every((k) => deepEqualUnordered(a[k], b[k]))
}

export const fieldMatchesQuery = (f, q) => {
    if (!q) return true
    const hay = `${f.name} ${f.description || ""}`.toLowerCase()
    if (hay.includes(q)) return true
    if (f.type === "object" && f.fields) {
        return f.fields.some((c) => fieldMatchesQuery(c, q))
    }
    return false
}

export const buildFieldsPreview = (fields) => {
    const obj = {}
    for (const f of fields || []) {
        if (!f.name) continue
        switch (f.type) {
            case "string":
                obj[f.name] = "string"
                break
            case "number":
                obj[f.name] = "number"
                break
            case "boolean":
                obj[f.name] = "boolean"
                break
            case "enum":
                obj[f.name] = f.options?.length ? f.options.join(" | ") : "enum"
                break
            case "array":
                obj[f.name] = [f.items_type || "string"]
                break
            case "object":
                obj[f.name] = buildFieldsPreview(f.fields || [])
                break
            default:
                break
        }
    }
    return obj
}
